import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  SparklesIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowRightIcon,
  Cog6ToothIcon,
  TrashIcon,
  PlusIcon,
  PencilIcon,
  ListBulletIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import studiesService, { Study, StudyModule, UpdateStudyDto } from '../services/studies';
import StudyFormModal from '../components/StudyFormModal';
import LoadingSpinner from '../components/LoadingSpinner';

const StudyModules: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [study, setStudy] = useState<Study | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newModuleTitle, setNewModuleTitle] = useState('');
  const [newModuleTopic, setNewModuleTopic] = useState('');
  const [creatingModule, setCreatingModule] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  const loadStudy = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await studiesService.getStudy(id);
      setStudy(data);
    } catch (error) {
      toast.error('Erro ao carregar o estudo');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadStudy();
  }, [loadStudy]);

  const handleGenerateModules = async () => {
    if (!id) return;
    try {
      setGenerating(true);
      const updated = await studiesService.generateModules(id);
      setStudy(updated);
      toast.success('Módulos gerados com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar módulos com IA');
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleModuleStatus = async (module: StudyModule) => {
    if (!id) return;
    const nextStatus = module.status === 'completed' ? 'not_started' : 'completed';
    try {
      const updated = await studiesService.updateModuleStatus(id, module._id, nextStatus);
      setStudy(updated);
    } catch (error) {
      toast.error('Erro ao atualizar módulo');
    }
  };

  const handleDeleteStudy = async () => {
    if (!id) return;
    try {
      setDeleting(true);
      await studiesService.deleteStudy(id);
      toast.success('Estudo excluído com sucesso!');
      navigate('/studies');
    } catch (error) {
      toast.error('Falha ao excluir estudo');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleUpdateStudy = async (data: UpdateStudyDto) => {
    if (!id) return;
    try {
      const updated = await studiesService.updateStudy(id, data);
      setStudy(updated);
      setShowEditModal(false);
      toast.success('Estudo atualizado com sucesso!');
    } catch (error) {
      toast.error('Falha ao atualizar estudo');
    }
  };

  const handleCreateModule = async () => {
    const topic = newModuleTopic.trim();
    if (!id || !newModuleTitle.trim() || !topic) return;
    try {
      setCreatingModule(true);
      const updated = await studiesService.createModule(id, {
        title: newModuleTitle.trim(),
        topics: [topic],
      });
      setStudy(updated);
      toast.success('Módulo criado com sucesso!');
      setShowCreateModal(false);
      setNewModuleTitle('');
      setNewModuleTopic('');
    } catch (error) {
      toast.error('Erro ao criar módulo');
    } finally {
      setCreatingModule(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!study) {
    return (
      <div className="max-w-6xl mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-8 text-center">
        <p className="text-gray-600 dark:text-gray-400">Estudo não encontrado.</p>
        <button
          onClick={() => navigate('/studies')}
          className="mt-4 inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Voltar para Estudos
        </button>
      </div>
    );
  }

  const modules = [...(study.modules || [])].sort(
    (a, b) => (b.order ?? 0) - (a.order ?? 0),
  );

  const groupedModules = modules.reduce<{ title: string; modules: StudyModule[] }[]>(
    (groups, module) => {
      const existingGroup = groups.find((group) => group.title === module.title);
      if (existingGroup) {
        existingGroup.modules.push(module);
      } else {
        groups.push({ title: module.title, modules: [module] });
      }
      return groups;
    },
    [],
  );

  return (
    <div className="max-w-6xl mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-8">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigate('/studies')}
          className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Voltar para Estudos
        </button>

        <div className="relative">
          <button
            onClick={() => setShowOptionsMenu(!showOptionsMenu)}
            className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
            title="Configurações"
          >
            <Cog6ToothIcon className="h-5 w-5" />
          </button>

          {showOptionsMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowOptionsMenu(false)}
              ></div>
              <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-20 overflow-hidden">
                <button
                  onClick={() => {
                    setShowOptionsMenu(false);
                    setShowEditModal(true);
                  }}
                  className="w-full flex items-center px-4 py-3 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Editar assunto e descrição
                </button>
                <button
                  onClick={() => {
                    setShowOptionsMenu(false);
                    setShowDeleteConfirm(true);
                  }}
                  className="w-full flex items-center px-4 py-3 text-sm text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Excluir Estudo
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Módulos de {study.subject || study.title}
        </h1>
        {study.description && (
          <p className="mt-1 text-gray-600 dark:text-gray-400">{study.description}</p>
        )}
      </div>

      {modules.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <SparklesIcon className="mx-auto h-12 w-12 text-purple-500" />
          <h3 className="mt-3 text-lg font-medium text-gray-900 dark:text-white">
            Nenhum módulo criado ainda
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Deixe a IA criar os módulos de estudo com base no assunto e na descrição, ou crie um módulo manualmente.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={handleGenerateModules}
              disabled={generating}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 disabled:opacity-50"
            >
              {generating ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                  Gerando módulos...
                </>
              ) : (
                <>
                  <SparklesIcon className="h-5 w-5 mr-2" />
                  Gerar Módulos com IA
                </>
              )}
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Criar Módulo Manualmente
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden self-start">
              <button
                onClick={() => setViewMode('list')}
                title="Visualização em lista"
                className={`inline-flex items-center whitespace-nowrap px-3 py-2 text-sm font-medium ${
                  viewMode === 'list'
                    ? 'bg-purple-600 text-white dark:bg-purple-500'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <ListBulletIcon className="h-4 w-4 mr-1.5" />
                Lista
              </button>
              <button
                onClick={() => setViewMode('grid')}
                title="Visualização em grade"
                className={`inline-flex items-center whitespace-nowrap px-3 py-2 text-sm font-medium border-l border-gray-300 dark:border-gray-600 ${
                  viewMode === 'grid'
                    ? 'bg-purple-600 text-white dark:bg-purple-500'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <Squares2X2Icon className="h-4 w-4 mr-1.5" />
                Grade
              </button>
            </div>

            <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-3">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center justify-center whitespace-nowrap px-3 py-2 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600"
              >
                <PlusIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                Criar Módulo
              </button>
              <button
                onClick={handleGenerateModules}
                disabled={generating}
                className="inline-flex items-center justify-center whitespace-nowrap px-3 py-2 text-sm font-medium rounded-md text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 border border-purple-200 dark:border-purple-800 disabled:opacity-50"
              >
                {generating ? (
                  <ArrowPathIcon className="h-4 w-4 mr-1.5 flex-shrink-0 animate-spin" />
                ) : (
                  <SparklesIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                )}
                Regenerar com IA
              </button>
            </div>
          </div>

          {viewMode === 'list' ? (
            <div className="space-y-4">
              {groupedModules.map((group) => (
                <div
                  key={group.title}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
                >
                  <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                      {group.title}
                    </h3>
                  </div>
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {group.modules.map((module) => (
                      <div
                        key={module._id}
                        onClick={() => navigate(`/studies/${id}/module/${module._id}`)}
                        className="flex items-start gap-3 p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        <div className="min-w-0 flex-1">
                          {module.description && (
                            <p
                              title={module.description}
                              className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2"
                            >
                              {module.description}
                            </p>
                          )}
                          {module.lastViewedAt && (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                              Última visita: {new Date(module.lastViewedAt).toLocaleString('pt-BR')}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleModuleStatus(module);
                          }}
                          title={module.status === 'completed' ? 'Marcar como não concluído' : 'Marcar como concluído'}
                          className="flex-shrink-0"
                        >
                          {module.status === 'completed' ? (
                            <CheckCircleIconSolid className="h-6 w-6 text-green-500" />
                          ) : (
                            <CheckCircleIcon className="h-6 w-6 text-gray-300 dark:text-gray-600 hover:text-green-500" />
                          )}
                        </button>
                        <ArrowRightIcon className="h-4 w-4 mt-1.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {modules.map((module, index) => (
                <div
                  key={module._id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5 flex-shrink-0 inline-flex items-center justify-center h-6 w-6 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-semibold">
                        {index + 1}
                      </span>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                        {module.title}
                      </h3>
                    </div>
                    <button
                      onClick={() => handleToggleModuleStatus(module)}
                      title={module.status === 'completed' ? 'Marcar como não concluído' : 'Marcar como concluído'}
                      className="flex-shrink-0 ml-2"
                    >
                      {module.status === 'completed' ? (
                        <CheckCircleIconSolid className="h-6 w-6 text-green-500" />
                      ) : (
                        <CheckCircleIcon className="h-6 w-6 text-gray-300 dark:text-gray-600 hover:text-green-500" />
                      )}
                    </button>
                  </div>
                  {module.description && (
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      {module.description}
                    </p>
                  )}
                  <div className="mt-3">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        module.status === 'completed'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {module.status === 'completed' ? (
                        <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                      ) : (
                        <ClockIcon className="h-3.5 w-3.5 mr-1" />
                      )}
                      {module.status === 'completed' ? 'Concluído' : 'Não Iniciado'}
                    </span>
                  </div>
                  <button
                    onClick={() => navigate(`/studies/${id}/module/${module._id}`)}
                    className="mt-4 w-full inline-flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 border border-purple-200 dark:border-purple-800"
                  >
                    Ir para o Módulo
                    <ArrowRightIcon className="h-4 w-4 ml-1.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative border border-gray-200 dark:border-gray-700 w-full max-w-sm p-5 shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-lg bg-white dark:bg-gray-800">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
                <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4">
                Excluir Estudo?
              </h3>
              <div className="mt-2 px-2 py-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Tem certeza que deseja excluir este estudo? Todos os módulos e conteúdos gerados serão perdidos. Esta ação não pode ser desfeita.
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm font-medium rounded-md shadow-sm hover:bg-gray-400 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 dark:focus:ring-offset-gray-800 transition-colors duration-200"
                  disabled={deleting}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteStudy}
                  className="px-4 py-2 bg-red-600 dark:bg-red-700 text-white text-sm font-medium rounded-md shadow-sm hover:bg-red-700 dark:hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-offset-gray-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={deleting}
                >
                  {deleting ? 'Excluindo...' : 'Excluir'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative border border-gray-200 dark:border-gray-700 w-full max-w-md p-5 shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-lg bg-white dark:bg-gray-800">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Criar Módulo Manualmente
            </h3>
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Nome do módulo *
                </label>
                <input
                  type="text"
                  value={newModuleTitle}
                  onChange={(e) => setNewModuleTitle(e.target.value)}
                  placeholder="Ex: Routing"
                  className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm px-3 py-2"
                  autoFocus
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Tópico *
                </label>
                <input
                  type="text"
                  value={newModuleTopic}
                  onChange={(e) => setNewModuleTopic(e.target.value)}
                  placeholder="Ex: useNavigate, useParams"
                  className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm px-3 py-2"
                  aria-label="Tópico"
                  required
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-5">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewModuleTitle('');
                  setNewModuleTopic('');
                }}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm font-medium rounded-md shadow-sm hover:bg-gray-400 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 dark:focus:ring-offset-gray-800 transition-colors duration-200"
                disabled={creatingModule}
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateModule}
                disabled={creatingModule || !newModuleTitle.trim() || !newModuleTopic.trim()}
                className="px-4 py-2 bg-purple-600 dark:bg-purple-500 text-white text-sm font-medium rounded-md shadow-sm hover:bg-purple-700 dark:hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-offset-gray-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creatingModule ? 'Criando...' : 'Criar Módulo'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showEditModal && (
        <StudyFormModal
          study={study}
          onSubmit={handleUpdateStudy}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </div>
  );
};

export default StudyModules;
